"use client";
import React from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

type Props = {
  open: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string; // e.g., Delete
  confirmType?: 'danger' | 'default';
  typedPhrase?: string; // e.g., DELETE
  onConfirm: () => Promise<void> | void;
  onOpenChange: (v: boolean) => void;
};

export function ConfirmDialog({ open, title = 'Confirm', message, confirmLabel = 'Confirm', confirmType = 'default', typedPhrase, onConfirm, onOpenChange }: Props) {
  const [busy, setBusy] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const mustType = Boolean(typedPhrase && typedPhrase.trim().length > 0);
  const allowed = !busy && (!mustType || typed.trim() === typedPhrase);

  async function confirm() {
    if (!allowed) return;
    try {
      setBusy(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
      setTyped('');
    }
  }

  function close() {
    if (busy) return; onOpenChange(false); setTyped('');
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
          </DialogTitle>
        </DialogHeader>
        {message && <p className="text-sm text-muted-foreground">{message}</p>}
        {mustType && (
          <div className="mt-3">
            <label className="block text-xs text-muted-foreground mb-1">Type {typedPhrase} to confirm</label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
              aria-label={`Type ${typedPhrase} to confirm`}
            />
          </div>
        )}
        <DialogFooter className="pt-2">
          <button className="rounded border px-3 py-1.5 text-sm" onClick={close} disabled={busy}>Cancel</button>
          <button
            className={`rounded px-3 py-1.5 text-sm text-white ${confirmType === 'danger' ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400' : 'bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-500'} `}
            onClick={confirm}
            disabled={!allowed}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDialog;

// ---- Context-based confirm/alert API ----
type ConfirmOptions = Omit<Partial<Props>, 'open' | 'onOpenChange' | 'onConfirm'> & {
  typedPhrase?: string;
  confirmLabel?: string;
  confirmType?: 'danger' | 'default';
};

type ConfirmCtx = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: { title?: string; message?: string; acknowledgeLabel?: string }) => Promise<void>;
};

const Ctx = React.createContext<ConfirmCtx | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [opts, setOpts] = React.useState<ConfirmOptions>({});
  const resolver = React.useRef<((v: boolean) => void) | null>(null);
  const isAlert = React.useRef(false);

  const onConfirm = React.useCallback(() => {
    if (resolver.current) resolver.current(true);
  }, []);

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v && resolver.current) {
      if (isAlert.current) {
        resolver.current(true);
      } else {
        resolver.current(false);
      }
      resolver.current = null;
      isAlert.current = false;
    }
  };

  const api = React.useMemo<ConfirmCtx>(() => ({
    confirm: (o) => new Promise<boolean>((resolve) => {
      isAlert.current = false;
      resolver.current = resolve;
      setOpts({
        title: o.title || 'Confirm',
        message: o.message || '',
        confirmLabel: o.confirmLabel || 'Confirm',
        confirmType: o.confirmType || 'default',
        typedPhrase: o.typedPhrase,
      });
      setOpen(true);
    }),
    alert: (o) => new Promise<void>((resolve) => {
      isAlert.current = true;
      resolver.current = () => resolve();
      setOpts({ title: o.title || 'Notice', message: o.message || '', confirmLabel: o.acknowledgeLabel || 'OK' });
      setOpen(true);
    }),
  }), []);

  return (
    <Ctx.Provider value={api}>
      {children}
      <ConfirmDialog
        open={open}
        title={opts.title}
        message={opts.message}
        confirmLabel={opts.confirmLabel}
        confirmType={opts.confirmType}
        typedPhrase={opts.typedPhrase}
        onConfirm={async () => { onConfirm(); setOpen(false); }}
        onOpenChange={onOpenChange}
      />
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useConfirm must be used within ConfirmProvider');
  return (options: ConfirmOptions) => v.confirm(options);
}

export function useAlert() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useAlert must be used within ConfirmProvider');
  return (options: { title?: string; message?: string; acknowledgeLabel?: string }) => v.alert(options);
}
